import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { observer } from 'mobx-react';
import MenuGroupItemsListRow from '../../components/MenuGroupItemsListRow/MenuGroupItemsListRow';
import { getItemsList, getItemsTableColumnsWidth } from '../../helpers/menuGroupEdit';
import menuGroupEditStore from '../../store/menuGroupEditStore';
import { MENU_GROUP_ITEMS_COLUMN_NAME } from '../../types/menuGroupEdit';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { DndSensors, dndAutoScrollOptionsDefault } from 'src/utils/dnd';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { IMenuGroupProduct } from '~/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/types/menuGroups';

const MenuGroupItemsList = observer((): JSX.Element => {
  const dndSensors = DndSensors();

  const { categories } = menuGroupEditStore.editableMenuGroup;
  const { selectedCategory, filters } = menuGroupEditStore;
  const { is_taker360: isTaker360Enabled } = appStore.settings || {};

  const items = getItemsList(categories, selectedCategory, filters.search);

  const [mainColumnWidth, secondaryColumnWidth] = getItemsTableColumnsWidth(filters.search.isFocused, !!isTaker360Enabled);

  const hanldeDragStart = ({ active }: DragStartEvent) => {
    menuGroupEditStore.setDraggingItem(+active.id);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    menuGroupEditStore.changeCategoryItemsSorting(+active.id, over ? +over.id : null);
  };

  const renderItemRow = (item: IMenuGroupProduct | undefined, index?: number) => (
    <MenuGroupItemsListRow
      id={item?.id || 0}
      key={item?.id || 'key-' + String(index)}
      item={item}
      isActiveSearch={filters.search.isFocused}
      isTaker360Enabled={isTaker360Enabled}
      columnsWidth={{ main: mainColumnWidth, secondary: secondaryColumnWidth }}
    />
  );

  if (!items?.length && filters.search.isFocused) {
    return (
      <div className="d-flex flex-column flex-grow-1 align-items-center justify-content-center full-width mb-15">
        <img src={formatImagePath(`${Icons.NoResults}.svg`)} alt="no_more_zones" className="mb-6" />
        <p className="family-medium text-size-14 color-gray2">{localeStore.t('no_items_found')}</p>
      </div>
    );
  }

  if (!items?.length && !menuGroupEditStore.loadingOnFetch) {
    return (
      <div className="pos-relative">
        {!menuGroupEditStore.validation.items && categories.length ? (
          <p className="family-regular text-size-14 color-red mt-6 pos-absolute">{localeStore.t('please_add_item')}</p>
        ) : null}
        <div className="d-flex flex-column justify-content-center align-items-center mt-8">
          <img src={formatImagePath(`${Icons.Items}.svg`)} alt="no_items_yet" className="image__no-found" />
          <p className="family-medium text-size-24 color-gray2">{localeStore.t('no_items_yet')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-grow-1 mt-4">
      <div className="d-flex flex-row mb-4 pr-4">
        <ColumnTitle<MENU_GROUP_ITEMS_COLUMN_NAME> title="item" className={`w-${mainColumnWidth}`} />
        {filters.search.isFocused && <ColumnTitle<MENU_GROUP_ITEMS_COLUMN_NAME> title="category" className={`w-${secondaryColumnWidth}`} />}
        <ColumnTitle title="order_type" className={`w-${secondaryColumnWidth}`} />
        <ColumnTitle<MENU_GROUP_ITEMS_COLUMN_NAME> title="price" className={`w-${secondaryColumnWidth}`} tooltipMessage="first_size_price_shown" />
        {isTaker360Enabled && (
          <ColumnTitle<MENU_GROUP_ITEMS_COLUMN_NAME>
            title="taker360_price"
            className={`w-${secondaryColumnWidth}`}
            tooltipMessage="taker360_prices_tooltip"
          />
        )}
        <ColumnTitle title="availability" className={`w-${secondaryColumnWidth}`} />
      </div>
      <DndContext
        id="items"
        autoScroll={dndAutoScrollOptionsDefault}
        modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor, restrictToParentElement]}
        sensors={dndSensors}
        onDragStart={hanldeDragStart}
        onDragCancel={() => menuGroupEditStore.setDraggingItem(null)}
        onDragEnd={handleDragEnd}
      >
        <ScrollableBlock scrollClassName="d-flex flex-column" className="overflow-y-scroll" showScrollbar scrollbarSize="normal">
          {menuGroupEditStore.loadingOnFetch ? emptyArray().map(renderItemRow) : items?.map(renderItemRow)}
        </ScrollableBlock>

        <DragOverlay>
          {menuGroupEditStore.draggingItem && (
            <MenuGroupItemsListRow
              id={0}
              key="draggable-item"
              item={menuGroupEditStore.draggingItem}
              className="menu-group-items-list-row__image-drag_active"
              dragIconClassName="cursor-grabbing"
              isActiveSearch={filters.search.isFocused}
              isTaker360Enabled={isTaker360Enabled}
              columnsWidth={{ main: mainColumnWidth, secondary: secondaryColumnWidth }}
            />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
});

export default MenuGroupItemsList;
